// Every word on admin's Referrals (decision 55, 13 Oct; docs/referrals-spec.md section 4), in 1 place: the
// page, its 3 tabs, the dialogs, the flashes, the database's refusals in words, the writing-rules warnings in
// the offer editor, and the lines the person page's card and the audit log show. No board draws any of it;
// the words are the spec's, for Adam's look. Pure: it imports only the date helpers, so the specs check every
// line on their own.
//
// Adam's writing rules hold throughout: British English, numerals, no exclamation marks or dashes, straight
// quotes, contractions, sentence case. A place is never a "deposit", and the Accelerator is a group.
import { uk } from '../../lib/format.js'
import { staffFirstName } from '../../lib/names.js'

const MONTHS3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// £12.34, £23, £1,234.50: the old site's fmtGBP, as Account's section writes it
export function gbp(v) {
  const n = v == null || Number.isNaN(Number(v)) ? 0 : Number(v)
  return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })
}
// 13 Oct 2026, in UK time
export function dayMonthYear(t) {
  if (!t) return ''
  const u = uk(t)
  return `${u.d} ${MONTHS3[u.m - 1]} ${u.y}`
}

export const STATUS_WORDS = { applied: 'Applied', joined: 'Joined', qualified: 'Qualified', paid: 'Paid', not_proceeding: 'Not proceeding' }
// in a sentence: "The introduction is qualified."
export const statusInSentence = s => (STATUS_WORDS[s] || s || '').toLowerCase()

export const REF = {
  title: 'Referrals',
  lede: "Who referred whom, what's owed, and who can refer what.",
  tabsLabel: 'Referrals',
  tabs: { introductions: 'Introductions', who: 'Who can refer', offers: 'Offers' },

  // Introductions
  owedTitle: 'Owed right now',
  owedLine: (name, amount) => `${name}, ${amount}`,
  // a refund on a line still qualified (the review of 18 Oct): on the line, in Owed right now and in Mark paid
  refunded: day => `Refunded ${day}`,
  owedRefunded: (amount, day) => `${amount} of it is for a sale refunded ${day}. Check before paying.`,
  statusLabel: 'Status',
  statusChips: [
    { key: '', label: 'All' },
    { key: 'needs_look', label: 'Needs a look' },
    { key: 'applied', label: 'Applied' },
    { key: 'joined', label: 'Joined' },
    { key: 'qualified', label: 'Qualified' },
    { key: 'paid', label: 'Paid' },
    { key: 'not_proceeding', label: 'Not proceeding' },
    { key: 'turned_down', label: 'Turned down' },
  ],
  productLabel: 'Product',
  allProducts: 'All products',
  addByHand: 'Add by hand',
  tableLabel: 'Introductions',
  columns: { referred: 'Referred', referrer: 'Referrer', product: 'Product', status: 'Status', amount: 'Amount', date: 'Date', actions: '' },
  rows: { referrer: 'Referrer', product: 'Product', amount: 'Amount', date: 'Date', reference: 'Reference', code: 'Code' },
  someone: 'Someone',
  none: 'None',
  noProduct: 'No product',
  change: 'Change',
  changeLabel: name => `Change who referred ${name}`,
  note: text => `Note: ${text}`,
  ref: reference => `Ref ${reference}`,
  empty: "Nothing here for this filter. Introductions through a member's link land here on their own.",
  flags: {
    not_ticked: () => 'Not ticked to refer it',
    self_referral: () => 'Their own link',
    second_claim: first => `${first || 'Someone else'} introduced them first`,
    to_check: () => 'From Stripe, to check',
    no_product: () => 'Its product was deleted',
  },
  turnedDown: 'Turned down',
  buttons: {
    accept: 'Accept', turnDown: 'Turn down', joined: 'Joined', qualify: 'Qualify', notProceeding: 'Not proceeding',
    markPaid: 'Mark paid', placeOn: 'Place it on', acceptAsWell: 'Accept as well', keepOpen: 'Keep it open',
    cancel: 'Cancel', save: 'Save', add: 'Add', pick: 'Pick',
  },
  pickProduct: 'Pick a product',
  secondClaimAsk: first => `${first || 'Someone else'} introduced them first. Accept this as well, so both count?`,
  flash: {
    joined: 'Marked joined.',
    notProceeding: 'Marked not proceeding.',
    qualified: amount => `Qualified. ${amount} locked.`,
    paid: 'Marked paid.',
    reassigned: 'Reassigned.',
    added: 'Introduction added.',
    accepted: first => `Accepted. It's on ${first}'s referrals now.`,
    turnedDown: first => `Turned down. ${first} doesn't see it.`,
  },

  // The dialogs
  qualify: {
    eyebrow: 'Qualify',
    title: 'Lock the amount',
    text: rule => `Only qualify once the condition has passed.${rule ? ` ${rule}` : ''}`,
    locks: "The amount locks and never recalculates, so a later price change can't touch it.",
    paid: 'What did they pay, before VAT? (£)',
    amount: 'Amount to pay, before VAT (£)',
    error: 'Type the amount in pounds.',
  },
  paid: {
    eyebrow: 'Mark paid',
    title: amount => `${amount} to pay`,
    text: 'Send the bank transfer first, then record it here with its reference so the ledger matches the bank.',
    reference: 'Payment reference (optional)',
    refunded: day => `This sale was refunded on ${day}. If it shouldn't be paid, cancel and mark it Not proceeding instead.`,
  },
  reassign: {
    eyebrow: 'Reassign',
    title: 'Who introduced them?',
    text: 'The first claim counts by default. Use this only to settle a genuine dispute.',
    search: 'Name or email',
    picked: name => `Picked: ${name}`,
    nobody: 'Nobody matches that.',
    looking: 'Looking.',
  },
  add: {
    eyebrow: 'Add by hand',
    title: 'Log an introduction by hand',
    text: 'For introductions that came outside the links: a name on the form, a WhatsApp message, a conversation.',
    who: 'Who introduced them',
    product: 'Product',
    name: 'Their name',
    email: 'Their email',
    status: 'Status',
    needWho: 'Pick who introduced them and the product.',
    needName: 'Add their name or email.',
  },

  // Who can refer
  whoLede: "The rule: they can refer it once they've done it. A ticked product shows on that person's Referrals in Account, with their link. An unticked one doesn't show at all.",
  productChips: 'Product',
  intakeLabel: 'Tick a whole intake',
  intakePick: 'Pick an intake',
  tickIntake: cohort => `Tick everyone in ${cohort}`,
  intakeTicked: n => `${n} ticked.`,
  intakeNone: 'Everyone in that intake is ticked already.',
  undo: 'Undo',
  undone: 'Undone. Nobody from that batch is ticked now.',
  undoLine: (n, cohort) => `${n} ticked in ${cohort}.`,
  whoColumns: { person: 'Person', done: "What shows they've done it", code: 'Code', can: 'Can refer' },
  whoTable: product => `Who can refer ${product}`,
  finished: day => `Finished ${day}`,
  certificate: n => `Certificate ${n}`,
  sessions: n => `${n} ${n === 1 ? 'session' : 'sessions'} attended`,
  byHand: 'Added by hand',
  cantLogIn: "Can't log in yet",
  noCode: 'No code yet',
  canRefer: (name, product) => `${name} can refer ${product}`,
  ticked: (first, product) => `${first} can refer ${product} now. Their link is on their Referrals.`,
  unticked: (first, product) => `${first} can't refer ${product} now. It's gone from their Referrals.`,
  addSomeone: 'Add someone else',
  addSomeonePlaceholder: 'Name or email',
  tick: 'Tick',
  nobodyHas: product => `Nobody has ${product} yet.`,
  noOffers: 'No product has an offer yet. Add 1 on Offers first.',

  // Offers
  shown: 'Shown',
  hidden: 'Hidden',
  edit: 'Edit',
  hide: 'Hide',
  show: 'Show',
  addOffer: 'Add an offer',
  hiddenFlash: 'Hidden from everyone ticked for it.',
  shownFlash: 'Shown to everyone ticked for it.',
  noLink: 'No link yet, so anyone ticked sees "Your link for this is on its way" until you add one.',
  via: { ref: 'Tally (ref)', client_reference_id: 'Stripe (client_reference_id)' },
  saved: 'Saved. Everyone ticked for it sees the new details now.',
  offerEyebrow: 'Offer',
  saveOffer: 'Save offer',
  fields: {
    link: 'Link, without any code',
    param: 'Link parameter',
    startsAs: 'A sale through the link starts as',
    fee: 'Fee',
    percent: 'Percent',
    amount: 'Amount (£, before VAT)',
    monthly: 'Monthly price (£, optional)',
    priceTerms: 'Price and terms line',
    qualifyRule: 'When it qualifies (shown under their link)',
    positioning: 'Positioning line',
    whoFor: "Who it's for (and who not to send)",
    keyPoints: 'Key points (1 per line, 3 to 5)',
    howTo: 'How to introduce it',
    neverSay: 'Never say (1 per line)',
    active: 'Shown to people ticked for it',
  },
  paramOptions: [
    { value: 'ref', label: 'ref (Tally)' },
    { value: 'client_reference_id', label: 'client_reference_id (Stripe)' },
  ],
  startsOptions: [
    { value: 'joined', label: 'Joined' },
    { value: 'qualified', label: 'Qualified' },
  ],
  feeOptions: [
    { value: 'first_month', label: 'Their first month' },
    { value: 'percentage', label: 'Percentage' },
    { value: 'flat', label: 'Flat amount' },
  ],
  errors: {
    link: 'The link must start with https:// and hold no code.',
    fee: 'Type the fee as a number.',
    percent: 'A percentage is 0 to 100.',
    qualified: 'Only a flat fee can start as qualified.',
    lines: 'Up to 10 lines.',
  },

  // The person page's card
  card: {
    title: 'Referrals',
    code: 'Their code',
    noCode: 'No code yet. 1 is made when you tick a product for them.',
    canRefer: 'Can refer',
    noOffers: 'No product has an offer yet.',
    theirs: 'Their introductions',
    none: "They haven't introduced anyone yet.",
    open: 'Open referrals',
    introducedBy: (name, product, day) => `Introduced by ${name} for ${product}, ${day}.`,
  },

  refusals: {
    no_person: "That person isn't in the hub any more.",
    no_offer: 'That product has no offer yet. Add 1 on Offers first.',
    no_cohort: "That intake isn't on this product.",
    no_batch: 'That batch has already been undone.',
    bad_offer: "That offer couldn't be saved. Check each field and try again.",
    bad_link: 'The link must start with https:// and hold no code.',
    bad_fee: 'Check the fee: a percentage is 0 to 100, and only a flat fee can start as qualified.',
    not_paid_product: 'Only a paid product can have an offer.',
    bad_status: "That status can't be set here.",
    name_or_email: 'Add their name or email.',
    bad_email: "That email doesn't look right.",
    no_line: "That introduction isn't there any more. Reload the page.",
    flag_open: 'Accept it or turn it down first.',
    not_from_here: 'Someone changed this a moment ago. Reload the page.',
    bad_amount: 'Type the amount in pounds, up to 100,000.',
    bad_reference: 'Keep the reference to 80 characters.',
    paid: 'A paid introduction stays with who was paid.',
    same_person: "Someone can't introduce themselves.",
    not_open: "That's already been decided.",
    needs_product: 'Pick the product it belongs on first.',
    old_codes_unreadable: "The old site's codes couldn't be read, so no code was made. Try again, and email adam@adamslee.co.uk if it happens again.",
  },
}

// A refusal in words: the database's code (kept by src/admin/api.js's referrals writes) in REF.refusals, else
// the AdminError's own line
export const refusal = e => REF.refusals[e?.code] || e?.message || 'Something went wrong. Try again.'

// The name staff screens use: the first name, as everywhere in admin
export const firstName = (row, prefix = '') => staffFirstName({ first_name: row?.[`${prefix}first_name`], full_name: row?.[`${prefix}full_name`] }) || 'them'

// A line a member may see (docs/referrals-spec.md 1.2): it has a product, and no flag or an accepted one
export const memberSees = l => Boolean(l.product_id) && (!l.flag || l.flag_state === 'accepted')

// A refund on a line that could still be paid (applied, joined or qualified): "Refunded 30 Sep 2026", or ''
export const refundWords = l => (l?.refunded_at && !['paid', 'not_proceeding'].includes(l.status) ? REF.refunded(dayMonthYear(l.refunded_at)) : '')

// The words under a line's status: its flag, or Turned down
export function flagWords(l) {
  if (l.flag_state === 'turned_down') return REF.turnedDown
  if (!l.flag || l.flag_state !== 'open') return ''
  const f = REF.flags[l.flag]
  return f ? f(l.first_referrer_full_name) : ''
}

// Which buttons a line gets (REF.buttons keys), by its state (spec 4.4, item 5)
export function lineActions(l) {
  if (l.flag_state === 'open') return l.flag === 'no_product' ? ['placeOn', 'accept', 'turnDown'] : ['accept', 'turnDown']
  switch (l.status) {
    case 'applied': return ['joined', 'qualify', 'notProceeding']
    case 'joined': return ['qualify', 'notProceeding']
    case 'qualified': return ['markPaid', 'notProceeding']
    default: return []
  }
}

// The filter: by status chip and product
export function filterLines(lines = [], { status = '', product = '' } = {}) {
  return lines.filter(l => {
    if (product && l.product_id !== product) return false
    if (!status) return true
    if (status === 'needs_look') return l.flag_state === 'open'
    if (status === 'turned_down') return l.flag_state === 'turned_down'
    return l.status === status && l.flag_state !== 'turned_down'
  })
}
export const needsLookCount = (lines = []) => lines.filter(l => l.flag_state === 'open').length

// Owed right now: per referrer, the qualified lines a member may see, largest first; each refunded line among them
// (refunded_at, src/admin/referrals/data.js) kept beside the total with its amount and day, so it's seen before paying
export function owedByReferrer(lines = []) {
  const by = new Map()
  for (const l of lines) {
    if (l.status !== 'qualified' || !memberSees(l)) continue
    const o = by.get(l.referrer_person_id) || { person_id: l.referrer_person_id, full_name: l.referrer_full_name, amount: 0, refunded: [] }
    o.amount = Math.round((o.amount + (Number(l.payout_amount) || 0)) * 100) / 100
    if (l.refunded_at) o.refunded.push({ id: l.id, amount: Number(l.payout_amount) || 0, at: l.refunded_at })
    by.set(l.referrer_person_id, o)
  }
  return [...by.values()].sort((a, b) => b.amount - a.amount || String(a.full_name).localeCompare(String(b.full_name)))
}

// The amount Qualify suggests: first month gives the monthly price, else the fee; flat gives the fee; a
// percentage is worked out from what they paid
export function suggestedAmount(offer, paid = null) {
  if (!offer) return ''
  if (offer.fee_kind === 'percentage') {
    const v = Number(paid)
    if (paid === null || paid === '' || Number.isNaN(v) || offer.fee_value == null) return ''
    return String(Math.round(v * Number(offer.fee_value)) / 100)
  }
  if (offer.fee_kind === 'first_month') return offer.price_monthly != null ? String(Number(offer.price_monthly)) : offer.fee_value != null ? String(Number(offer.fee_value)) : ''
  return offer.fee_value != null ? String(Number(offer.fee_value)) : ''
}

// Pounds typed in a field: 12.34, 12, £12.34 and 1,234.50 are numbers to 2 places; anything else is null
export function poundsOf(text) {
  const s = String(text ?? '').trim().replace(/^£/, '').replace(/,/g, '')
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(s)) return null
  const n = Number(s)
  return n >= 0 && n <= 100000 ? n : null
}

// What shows they've done it, on Who can refer, joined with full stops
export function doneSignals(c) {
  const out = []
  if (c.cohort_name) out.push(c.cohort_name)
  if (c.finished_at) out.push(REF.finished(dayMonthYear(c.finished_at)))
  if (c.certificate_number) out.push(REF.certificate(c.certificate_number))
  if (c.sessions_attended > 0) out.push(REF.sessions(c.sessions_attended))
  if (!c.access_status) out.push(REF.byHand)
  if (c.can_log_in === false) out.push(REF.cantLogIn)
  return out.length ? `${out.join('. ')}.` : ''
}

// An offer's summary line on Offers: "{price_terms}. {fee}. Through {via}."
export function feeWords(o) {
  if (!o || o.fee_value == null) return 'No fee set'
  if (o.fee_kind === 'first_month') return `Pays ${gbp(o.fee_value)}, their first month`
  if (o.fee_kind === 'percentage') return `Pays ${Number(o.fee_value)}%`
  return `Pays ${gbp(o.fee_value)}`
}
export function offerSummary(o) {
  const terms = String(o?.price_terms || '').trim().replace(/\.$/, '')
  return [terms, feeWords(o), `Through ${REF.via[o?.ref_param] || REF.via.client_reference_id}`].filter(Boolean).map(s => `${s}.`).join(' ')
}

// The video's own name, the 1 place the word deposit may stand (decision 44)
export const VIDEO_NAME = 'Buy your next property without a deposit'

// The writing-rules warnings under a text field as Adam types (spec 4.6, default 6). He can still save. Every offer
// field shows on a member's card, so the video's own name never warns, and "masterclass", which is internal, does.
export function writingWarnings(text) {
  const s = String(text || '')
  const out = []
  if (/[–—]/.test(s)) out.push('This has a dash. Use a full stop or a comma instead.')
  if (s.includes('!')) out.push('This has an exclamation mark.')
  if (/[‘’“”]/.test(s)) out.push('This has curly quotes. Use straight ones.')
  if (/deposit/i.test(s.replace(new RegExp(VIDEO_NAME, 'gi'), ''))) out.push('This says deposit. A place is secured, never a deposit.')
  if (/\broom\b/i.test(s)) out.push('This says room. The Accelerator is a group.')
  if (/\bmasterclass\b/i.test(s)) out.push("This says masterclass. Members only ever see the video's own name.")
  // CLAUDE.md: the Accelerator's price is the price on the day they join, never a lock (the review of 18 Oct)
  if (/\block(s|ed|ing)?\b/i.test(s)) out.push("This says lock. A price is the price on the day they join, never a lock.")
  return out
}

// The offer editor's checks before anything is sent (the database checks the same and refuses with its own
// codes): { field: words } for each field that's wrong, or {} when it can be saved
export function offerErrors(f) {
  const errors = {}
  const link = String(f.intake_link || '').trim()
  if (link && (!/^https:\/\/\S+$/.test(link) || link.length > 500 || /[?&](ref|client_reference_id)=/.test(link))) errors.intake_link = REF.errors.link
  const num = v => String(v ?? '').trim()
  const isNum = v => /^\d{1,8}(\.\d{1,2})?$/.test(num(v))
  if (num(f.fee_value) && !isNum(f.fee_value)) errors.fee_value = REF.errors.fee
  else if (f.fee_kind === 'percentage' && num(f.fee_value) && Number(f.fee_value) > 100) errors.fee_value = REF.errors.percent
  if (num(f.price_monthly) && !isNum(f.price_monthly)) errors.price_monthly = REF.errors.fee
  if (f.ref_param === 'client_reference_id' && f.starts_as === 'qualified' && f.fee_kind !== 'flat') errors.starts_as = REF.errors.qualified
  const lines = t => String(t || '').split('\n').map(x => x.trim()).filter(Boolean)
  if (lines(f.key_points).length > 10) errors.key_points = REF.errors.lines
  if (lines(f.never_say).length > 10) errors.never_say = REF.errors.lines
  return errors
}

// The offer editor's form from a saved offer (or a new 1), and the body save_referral_offer takes from it
export function offerForm(o) {
  return {
    intake_link: o?.intake_link || '',
    ref_param: o?.ref_param || 'client_reference_id',
    starts_as: o?.starts_as === 'qualified' ? 'qualified' : 'joined',
    fee_kind: o?.fee_kind || 'flat',
    fee_value: o?.fee_value != null ? String(Number(o.fee_value)) : '',
    price_monthly: o?.price_monthly != null ? String(Number(o.price_monthly)) : '',
    price_terms: o?.price_terms || '',
    qualify_rule: o?.qualify_rule || '',
    positioning_line: o?.positioning_line || '',
    who_its_for: o?.who_its_for || '',
    key_points: (o?.key_points || []).join('\n'),
    how_to_introduce: o?.how_to_introduce || '',
    never_say: (o?.never_say || []).join('\n'),
    is_active: o ? o.is_active !== false : true,
  }
}
export function offerBody(f) {
  const lines = t => String(t || '').split('\n').map(x => x.trim()).filter(Boolean)
  const num = v => (String(v ?? '').trim() === '' ? null : String(v).trim())
  return {
    intake_link: String(f.intake_link || '').trim(),
    ref_param: f.ref_param,
    starts_as: f.ref_param === 'ref' ? 'applied' : f.starts_as,
    fee_kind: f.fee_kind,
    fee_value: num(f.fee_value),
    price_monthly: num(f.price_monthly),
    price_terms: f.price_terms.trim(),
    qualify_rule: f.qualify_rule.trim(),
    positioning_line: f.positioning_line.trim(),
    who_its_for: f.who_its_for.trim(),
    key_points: lines(f.key_points),
    how_to_introduce: f.how_to_introduce.trim(),
    never_say: lines(f.never_say),
    is_active: Boolean(f.is_active),
  }
}

// The audit log's and History's words for the 15 referral actions (migration 79's audit_log_action)
export const AUDIT_ACTIONS = {
  referral_code_made: 'Referral code made',
  referral_ticked: 'Can refer a product',
  referral_unticked: "Can't refer a product now",
  referral_intake_ticked: 'Intake ticked to refer a product',
  referral_batch_undone: 'Intake ticks undone',
  referral_offer_saved: 'Referral offer saved',
  introduction_recorded: 'Introduction recorded',
  introduction_added: 'Introduction added by hand',
  introduction_status: 'Introduction status changed',
  introduction_qualified: 'Introduction qualified',
  introduction_paid: 'Introduction marked paid',
  introduction_reassigned: 'Introduction reassigned',
  introduction_accepted: 'Flagged introduction accepted',
  introduction_turned_down: 'Flagged introduction turned down',
  referrals_copied: 'Referrals copied from the old site',
}

// A History line for 1 referrals audit row: { action, after, before } with the product's name. null for
// another action.
export function historyWords(action, after = {}, product = 'a product') {
  switch (action) {
    case 'referral_code_made': return 'Referral code made'
    case 'referral_ticked': return `Can refer ${product}`
    case 'referral_unticked': return `Can't refer ${product} now`
    case 'referral_intake_ticked': return `Intake ticked to refer ${product}`
    case 'referral_batch_undone': return 'Intake ticks undone'
    case 'referral_offer_saved': return 'Referral offer saved'
    case 'introduction_recorded': return `Introduction recorded from ${after?.source === 'tally' ? 'Tally' : 'Stripe'}`
    case 'introduction_added': return 'Introduction added by hand'
    case 'introduction_status': return `Introduction marked ${statusInSentence(after?.status) || 'changed'}`
    case 'introduction_qualified': return after?.payout_amount != null ? `Introduction qualified, ${gbp(after.payout_amount)} locked` : 'Introduction qualified'
    case 'introduction_paid': return 'Introduction marked paid'
    case 'introduction_reassigned': return 'Introduction reassigned'
    case 'introduction_accepted': return 'Flagged introduction accepted'
    case 'introduction_turned_down': return 'Flagged introduction turned down'
    case 'referrals_copied': return 'Referrals copied from the old site'
    default: return null
  }
}
